import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper to get full image URL (handles both old /uploads/ and new /api/uploads/ paths)
export const getImageUrl = (path) => {
  if (!path) return null;
  // If it's a data URL (base64), return as-is
  if (path.startsWith('data:')) {
    return path;
  }
  // If it's an S3 URL, route through the file proxy to get a pre-signed URL
  if (path.includes('.s3.') && path.includes('amazonaws.com')) {
    return `${BACKEND_URL}/api/files/serve?url=${encodeURIComponent(path)}`;
  }
  // If it's already a full URL, return as-is
  if (path.startsWith('http://') || path.startsWith('https://')) {
    return path;
  }
  // If it's an old-style /uploads/ path, convert to /api/uploads/
  if (path.startsWith('/uploads/')) {
    return `${BACKEND_URL}/api${path}`;
  }
  // If it's already /api/uploads/, add the backend URL
  if (path.startsWith('/api/uploads/')) {
    return `${BACKEND_URL}${path}`;
  }
  // Default: assume it's a relative path
  return `${BACKEND_URL}${path}`;
};

// Date formatting utilities - treats dates as Eastern Time
// This fixes the issue where dates like "2026-03-27" were showing as March 26th
export const formatDateET = (dateStr, options = {}) => {
  if (!dateStr) return '';
  
  // Parse the date string and treat it as a date without timezone conversion
  // For date-only strings like "2026-03-27", append time to prevent UTC interpretation
  let dateString = dateStr;
  if (dateStr.length === 10) {
    // Date-only string (YYYY-MM-DD) - treat as noon ET to avoid day shifting
    dateString = `${dateStr}T12:00:00`;
  }
  
  const date = new Date(dateString);
  
  const defaultOptions = {
    timeZone: 'America/New_York',
    ...options
  };
  
  return date.toLocaleDateString('en-US', defaultOptions);
};

// Format date for display with various formats
export const formatEventDate = (dateStr) => {
  if (!dateStr) return { day: '', month: '', full: '' };
  
  // For date-only strings, append time to prevent UTC shift
  let dateString = dateStr;
  if (dateStr.length === 10) {
    dateString = `${dateStr}T12:00:00`;
  }
  
  const date = new Date(dateString);
  const options = { timeZone: 'America/New_York' };
  
  return {
    day: date.toLocaleDateString('en-US', { ...options, day: 'numeric' }),
    month: date.toLocaleDateString('en-US', { ...options, month: 'short' }).toUpperCase(),
    full: date.toLocaleDateString('en-US', { ...options, weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })
  };
};

// Check if event is upcoming (in Eastern Time)
export const isUpcomingEvent = (dateStr) => {
  if (!dateStr) return false;
  
  let dateString = dateStr;
  if (dateStr.length === 10) {
    dateString = `${dateStr}T23:59:59`;
  }
  
  const eventDate = new Date(dateString);
  const now = new Date();
  
  return eventDate >= now;
};

// Create axios instance
const api = axios.create({
  baseURL: API
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('adminToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 errors - clear token and redirect to login
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('adminToken');
      // Only redirect if we're on an admin page
      if (window.location.pathname.startsWith('/admin') && !window.location.pathname.includes('/login')) {
        window.location.href = '/admin/login';
      }
    }
    return Promise.reject(error);
  }
);

// Auth
export const adminLogin = async (username, password) => {
  const response = await axios.post(`${API}/admin/login`, { username, password });
  return response.data;
};

export const createFirstAdmin = async (username, password) => {
  const response = await axios.post(`${API}/admin/create-first`, { username, password });
  return response.data;
};

// Artists
export const getArtists = async () => {
  const response = await axios.get(`${API}/artists`);
  return response.data;
};

export const getArtistBySlug = async (slug) => {
  const response = await axios.get(`${API}/artists/${slug}`);
  return response.data;
};

export const getArtistReleases = async (slug) => {
  const response = await axios.get(`${API}/artists/${slug}/releases`);
  return response.data;
};

export const createArtist = async (artistData) => {
  const response = await api.post('/admin/artists', artistData);
  return response.data;
};

export const updateArtist = async (artistId, artistData) => {
  const response = await api.put(`/admin/artists/${artistId}`, artistData);
  return response.data;
};

export const deleteArtist = async (artistId) => {
  const response = await api.delete(`/admin/artists/${artistId}`);
  return response.data;
};

// Releases
export const getReleases = async () => {
  const response = await axios.get(`${API}/releases`);
  return response.data;
};

export const createRelease = async (releaseData) => {
  const response = await api.post('/admin/releases', releaseData);
  return response.data;
};

export const updateRelease = async (releaseId, releaseData) => {
  const response = await api.put(`/admin/releases/${releaseId}`, releaseData);
  return response.data;
};

export const deleteRelease = async (releaseId) => {
  const response = await api.delete(`/admin/releases/${releaseId}`);
  return response.data;
};

export const getRelease = async (releaseId) => {
  const response = await axios.get(`${API}/releases/${releaseId}`);
  return response.data;
};

export const getReleaseLinks = async (releaseId) => {
  const response = await axios.get(`${API}/releases/${releaseId}/links`);
  return response.data;
};

// Appearances
export const getAppearances = async () => {
  const response = await axios.get(`${API}/appearances`);
  return response.data;
};

export const getArtistAppearances = async (slug) => {
  const response = await axios.get(`${API}/artists/${slug}/appearances`);
  return response.data;
};

export const createAppearance = async (data) => {
  const response = await api.post('/admin/appearances', data);
  return response.data;
};

export const updateAppearance = async (appearanceId, data) => {
  const response = await api.put(`/admin/appearances/${appearanceId}`, data);
  return response.data;
};

export const deleteAppearance = async (appearanceId) => {
  const response = await api.delete(`/admin/appearances/${appearanceId}`);
  return response.data;
};

export const getAdminAppearances = async () => {
  const response = await api.get('/admin/appearances');
  return response.data;
};

// Events
export const getEvents = async () => {
  const response = await axios.get(`${API}/events`);
  return response.data;
};

export const getEvent = async (eventId) => {
  const response = await axios.get(`${API}/events/${eventId}`);
  return response.data;
};

export const createEvent = async (eventData) => {
  const response = await api.post('/admin/events', eventData);
  return response.data;
};

export const updateEvent = async (eventId, eventData) => {
  const response = await api.put(`/admin/events/${eventId}`, eventData);
  return response.data;
};

export const deleteEvent = async (eventId) => {
  const response = await api.delete(`/admin/events/${eventId}`);
  return response.data;
};

export const getEventRegistrations = async (eventId) => {
  const response = await api.get(`/admin/events/${eventId}/registrations`);
  return response.data;
};

export const registerForEvent = async (eventId, registrationData) => {
  const response = await axios.post(`${API}/events/${eventId}/register`, registrationData);
  return response.data;
};

// File Upload
export const uploadFile = async (file) => {
  const formData = new FormData();
  formData.append('file', file);
  
  try {
    const response = await api.post('/admin/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      // Increase timeout for large files
      timeout: 60000,
      // Track upload progress
      onUploadProgress: (progressEvent) => {
        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        console.log(`Upload progress: ${percentCompleted}%`);
      }
    });
    return response.data;
  } catch (error) {
    console.error('Upload failed:', {
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data,
      message: error.message
    });
    throw error;
  }
};

// Newsletter
export const subscribeNewsletter = async (email) => {
  const response = await axios.post(`${API}/newsletter/subscribe`, { email });
  return response.data;
};

// Newsletter Admin
export const getNewsletterSubscribers = async () => {
  const response = await api.get('/admin/newsletter');
  return response.data;
};

export const deleteNewsletterSubscriber = async (subscriberId) => {
  const response = await api.delete(`/admin/newsletter/${subscriberId}`);
  return response.data;
};

// Contact Form
export const submitContactForm = async (data) => {
  const response = await axios.post(`${API}/contact`, data);
  return response.data;
};

// Contact Messages Admin
export const getContactMessages = async () => {
  const response = await api.get('/admin/contact-messages');
  return response.data;
};

export const updateMessageStatus = async (messageId, status) => {
  const response = await api.put(`/admin/contact-messages/${messageId}/status?status=${status}`);
  return response.data;
};

export const deleteContactMessage = async (messageId) => {
  const response = await api.delete(`/admin/contact-messages/${messageId}`);
  return response.data;
};


export default api;
